import { Theme } from '@mui/material/styles/createTheme';
import { ReactNode } from 'react';
import { formatToPhoneNumber } from 'src/components';
import { ActivityMessage } from 'src/components/ActivityList/activity-list-item';
import { BoldText } from 'src/components/ActivityList/styled/bold-text';
import { ActivityFeed } from 'src/graphql/entities/activity-feed/hooks/use-activity-feed';

export const buildClientMessage = (activity: ActivityFeed): ActivityMessage[] => {
  const actorName = activity.actor?.name?.trim() || '<Platform User>';

  switch (activity.activityType) {
    case 'create':
      return [
        {
          id: activity.id,
          icon: 'flare',
          color: (theme: Theme) => theme.palette.custom.OV.sys.primary,
          message: `${actorName} created the client`,
          timestamp: activity.timestamp,
        },
      ];
    case 'update':
      return activity.changes
        .filter(({ value, previousValue }) => value !== previousValue)
        .map((activityChange) => {
          const { name, icon: customIcon, customType } = activityChange;
          const icon = customIcon || 'edit_attributes';
          const value = formatToCustomType(activityChange?.value, customType);
          const previousValue = formatToCustomType(activityChange?.previousValue, customType);
          if (!!value && !!previousValue) {
            return {
              id: `${activity.id}-${name}`,
              icon,
              color: (theme: Theme) => theme.palette.custom.OV.sys.tertiary,
              message: (
                <>
                  {actorName} changed the <BoldText>{name}</BoldText> from{' '}
                  <BoldText>{previousValue}</BoldText> to <BoldText>{value}</BoldText>
                </>
              ),
              timestamp: activity.timestamp,
            };
          } else if (!!value && !previousValue) {
            return {
              id: `${activity.id}-${name}`,
              icon,
              color: (theme: Theme) => theme.palette.custom.OV.sys.tertiary,
              message: (
                <>
                  {actorName} added the {name} <BoldText>{value}</BoldText>
                </>
              ),
              timestamp: activity.timestamp,
            };
          } else if (!value && !!previousValue) {
            return {
              id: `${activity.id}-${name}`,
              icon,
              color: (theme: Theme) => theme.palette.custom.OV.sys.tertiary,
              message: (
                <>
                  {actorName} removed the {name}{' '}
                  <BoldText>&quot;{previousValue}&quot;</BoldText>
                </>
              ),
              timestamp: activity.timestamp,
            };
          }
          return null;
        })
        .filter((value) => !!value);
    default:
      return [];
  }
};

function formatToCustomType(value: string | null, customType?: string): ReactNode {
  if (!value || !customType) {
    return value;
  }

  switch (customType) {
    case 'PhoneNumber':
      return formatToPhoneNumber(value);
    default:
      return value;
  }
}
